import React, { useEffect, useRef, useState, useContext } from "react";
import { useParams } from "react-router-dom";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import { 
  Grid, 
  LinearProgress, 
  Typography, 
  Button, 
  Divider, 
  Card, 
  CardHeader, 
  CardContent, 
  Box, 
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Avatar,
  Tooltip
} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import SendIcon from "@material-ui/icons/Send";
import PhoneIcon from "@material-ui/icons/Phone";
import MessageIcon from "@material-ui/icons/Message";
import ScheduleIcon from "@material-ui/icons/Schedule";
import EventAvailableIcon from "@material-ui/icons/EventAvailable";
import DoneAllIcon from "@material-ui/icons/DoneAll";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import ListAltIcon from "@material-ui/icons/ListAlt";
import GetAppIcon from "@material-ui/icons/GetApp";
import { useDate } from "../../hooks/useDate";
import usePlans from "../../hooks/usePlans";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title as ChartTitle,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Doughnut } from 'react-chartjs-2';

// Registrar componentes do Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  ChartTitle,
  ChartTooltip,
  Legend,
  Filler
);

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
    marginBottom: theme.spacing(2),
  },
  textRight: {
    textAlign: "right",
  },
  tabPanelsContainer: {
    padding: theme.spacing(2),
  },
  summaryCards: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  summaryCard: {
    padding: theme.spacing(2),
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  cardHeader: {
    paddingBottom: 0,
  },
  cardIcon: {
    color: theme.palette.primary.main,
    fontSize: 40,
    marginBottom: theme.spacing(1),
  },
  progressContainer: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  tableContainer: {
    marginTop: theme.spacing(3),
  },
  tableTitle: {
    margin: theme.spacing(2, 0),
    fontWeight: 500,
  },
  chartContainer: {
    height: 300,
    marginBottom: theme.spacing(4),
  },
  chip: {
    margin: theme.spacing(0.5),
  },
  statusChip: {
    color: '#fff',
    fontWeight: 'bold',
  },
  deliveredChip: {
    backgroundColor: theme.palette.success.main,
  },
  pendingChip: {
    backgroundColor: theme.palette.warning.main,
  },
  failedChip: {
    backgroundColor: theme.palette.error.main,
  },
  pieChartContainer: {
    height: 250,
    position: 'relative',
    marginTop: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: 500,
    margin: theme.spacing(2, 0),
  },
  detailsCard: {
    marginBottom: theme.spacing(2),
  },
  fullWidthGrid: {
    width: '100%',
  },
  avatar: {
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
  },
}));

const CampaignProReport = () => {
  const classes = useStyles();
  const history = useHistory();

  const { campaignId } = useParams();

  const [campaign, setCampaign] = useState({});
  const [delivered, setDelivered] = useState(0);
  const [loading, setLoading] = useState(false);
  const [messageRows, setMessageRows] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [uniqueNumbers, setUniqueNumbers] = useState(0);
  const [totalMessagesSent, setTotalMessagesSent] = useState(0);
  const mounted = useRef(true);
  const { user, socket } = useContext(AuthContext);

  const { datetimeToClient } = useDate();
  const { getPlanCompany } = usePlans();
  
  // Dados para o gráfico de pizza
  const [chartData, setChartData] = useState({
    labels: ['Entregues', 'Aguardando', 'Falhas'],
    datasets: [
      {
        data: [0, 0, 0],
        backgroundColor: [
          'rgba(75, 192, 192, 0.7)',
          'rgba(255, 206, 86, 0.7)',
          'rgba(255, 99, 132, 0.7)',
        ],
        borderColor: [
          'rgba(75, 192, 192, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(255, 99, 132, 1)',
        ],
        borderWidth: 1,
      },
    ],
  });
  
  // Opções para o gráfico de pizza
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
      },
    },
  };

  useEffect(() => {
    async function fetchData() {
      const companyId = user.companyId;
      const planConfigs = await getPlanCompany(undefined, companyId);
      if (!planConfigs.plan.useCampaigns) {
        toast.error("Esta empresa não possui permissão para acessar essa página! Estamos lhe redirecionando.");
        setTimeout(() => {
          history.push(`/`)
        }, 1000);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (mounted.current) {
      findCampaign();
    }

    return () => {
      mounted.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const companyId = user.companyId;

    const onCampaignEvent = (data) => {
      if (data.record.id === +campaignId) {
        setCampaign(data.record);

        if (data.record.status === "FINALIZADA") {
          setTimeout(() => {
            findCampaign();
          }, 5000);
        }
      }
    };
    socket.on(`company-${companyId}-CampaignPro`, onCampaignEvent);

    return () => {
      socket.off(`company-${companyId}-CampaignPro`, onCampaignEvent);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  const findCampaign = async () => {
    try {
      setLoading(true);
      
      // Buscar dados básicos da campanha PRO
      const { data: campaignData } = await api.get(`/campaigns-pro/${campaignId}`);
      setCampaign(campaignData);
      
      // Buscar estatísticas separadamente
      try {
        const { data: stats } = await api.get(`/campaigns-pro/${campaignId}/stats`);
        setDelivered(stats.deliveredMessages);
        setUniqueNumbers(stats.uniqueNumbers);
        setTotalMessagesSent(stats.totalMessages);
        
        // Atualizar dados do gráfico
        setChartData({
          labels: ['Entregues', 'Aguardando', 'Falhas'],
          datasets: [
            {
              data: [stats.deliveredMessages, stats.pendingMessages, stats.failedMessages],
              backgroundColor: [
                'rgba(75, 192, 192, 0.7)',
                'rgba(255, 206, 86, 0.7)',
                'rgba(255, 99, 132, 0.7)',
              ],
              borderColor: [
                'rgba(75, 192, 192, 1)',
                'rgba(255, 206, 86, 1)',
                'rgba(255, 99, 132, 1)',
              ],
              borderWidth: 1,
            },
          ],
        });
      } catch (statsError) {
        console.error("Erro ao buscar estatísticas:", statsError);
        setDelivered(0);
        setUniqueNumbers(0);
        setTotalMessagesSent(0);
      }
      
      // Buscar dados de shipping com paginação
      try {
        const { data: shippingResponse } = await api.get(`/campaigns-pro/${campaignId}/shipping?page=1&pageSize=${rowsPerPage}`);
        
        if (shippingResponse && shippingResponse.shipping) {
          const shippingData = shippingResponse.shipping;
          
          // Processar dados para a tabela
          const formattedRows = shippingData.map(item => ({
            id: item.id,
            jobId: item.id,
            number: item.number,
            message: item.message ? item.message.substring(0, 50) + (item.message.length > 50 ? '...' : '') : '',
            fullMessage: item.message || '',
            canal: item.whatsapp?.name || '-',
            deliveredAt: item.deliveredAt ? datetimeToClient(item.deliveredAt) : null,
            createdAt: item.createdAt ? datetimeToClient(item.createdAt) : null,
            status: item.deliveredAt ? 'delivered' : 'pending'
          }));
          
          setMessageRows(formattedRows);
        }
      } catch (shippingError) {
        console.error("Erro ao buscar dados de envio:", shippingError);
        setMessageRows([]);
      }
      
    } catch (error) {
      console.error("Erro ao buscar campanha:", error);
      toast.error("Erro ao carregar relatório da campanha PRO");
    } finally {
      setLoading(false);
    }
  };

  const formatStatus = (val) => {
    switch (val) {
      case "INATIVA":
        return "Inativa";
      case "PROGRAMADA":
        return "Programada";
      case "EM_ANDAMENTO":
        return "Em Andamento";
      case "CANCELADA":
        return "Cancelada";
      case "FINALIZADA":
        return "Finalizada";
      default:
        return val;
    }
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    loadMoreShipping(1, parseInt(event.target.value, 10));
  };
  
  // Função para carregar mais dados de shipping
  const loadMoreShipping = async (newPage = 1, pageSize = 50) => {
    try {
      setLoading(true);
      
      const url = `/campaigns-pro/${campaignId}/shipping?page=${newPage}&pageSize=${pageSize}`;
      const { data: shippingResponse } = await api.get(url);
      
      if (shippingResponse && shippingResponse.shipping) {
        const shippingData = shippingResponse.shipping;
        
        const formattedRows = shippingData.map(item => ({
          id: item.id,
          jobId: item.id,
          number: item.number,
          message: item.message ? item.message.substring(0, 50) + (item.message.length > 50 ? '...' : '') : '',
          fullMessage: item.message || '',
          canal: item.whatsapp?.name || '-',
          deliveredAt: item.deliveredAt ? datetimeToClient(item.deliveredAt) : null,
          createdAt: item.createdAt ? datetimeToClient(item.createdAt) : null,
          status: item.deliveredAt ? 'delivered' : 'pending'
        }));
        
        setMessageRows(formattedRows);
        
      } else {
        setMessageRows([]);
      }
    } catch (error) {
      console.error("Erro ao carregar mais dados de envio:", error);
      toast.error("Erro ao carregar dados de envio");
      setMessageRows([]);
    } finally {
      setLoading(false);
    }
  };
  
  // Função para formatar o número de telefone
  const formatPhoneNumber = (number) => {
    if (!number) return '';
    
    if (number.length > 15) {
      return `Grupo (${number.substring(0, 6)}...)`;
    }
    
    if (number.length === 12 && number.startsWith('55')) {
      const ddd = number.substring(2, 4);
      const firstPart = number.substring(4, 9);
      const lastPart = number.substring(9);
      return `+55 (${ddd}) ${firstPart}-${lastPart}`;
    }
    
    return number;
  };
  
  // Função para obter o status do envio
  const getMessageStatusChip = (status) => {
    switch (status) {
      case 'delivered':
        return (
          <Chip 
            label="Entregue" 
            size="small" 
            icon={<DoneAllIcon fontSize="small" />} 
            className={`${classes.statusChip} ${classes.deliveredChip}`} 
          />
        );
      case 'pending':
        return (
          <Chip 
            label="Pendente" 
            size="small" 
            icon={<ScheduleIcon fontSize="small" />} 
            className={`${classes.statusChip} ${classes.pendingChip}`} 
          />
        );
      case 'failed':
        return (
          <Chip 
            label="Falha" 
            size="small" 
            icon={<ErrorIcon fontSize="small" />} 
            className={`${classes.statusChip} ${classes.failedChip}`} 
          />
        );
      default:
        return (
          <Chip 
            label="Desconhecido" 
            size="small" 
            variant="outlined" 
          />
        );
    }
  };

  // Função para exportar dados para Excel
  const exportToExcel = async () => {
    try {
      toast.info("Gerando relatório Excel, aguarde...");
      
      let allShippingData = [];
      let currentPage = 1;
      let hasMoreData = true;
      const pageSize = 1000;
      
      while (hasMoreData) {
        const { data: shippingResponse } = await api.get(`/campaigns-pro/${campaignId}/shipping?page=${currentPage}&pageSize=${pageSize}`);
        
        if (!shippingResponse || !shippingResponse.shipping) {
          break;
        }
        
        allShippingData = [...allShippingData, ...shippingResponse.shipping];
        
        hasMoreData = currentPage < shippingResponse.totalPages;
        currentPage++;
        
        toast.info(`Carregando dados... ${allShippingData.length} registros encontrados`);
      }
      
      if (allShippingData.length === 0) {
        toast.error("Nenhum dado encontrado para exportar");
        return;
      }

      const shippingData = allShippingData;
      
      const excelData = [
        ["Relatório de Campanha PRO", "", "", "", "", "", ""],
        [`Campanha: ${campaign.name}`, "", "", "", "", "", ""],
        [`Data de Geração: ${new Date().toLocaleString('pt-BR')}`, "", "", "", "", "", ""],
        ["", "", "", "", "", "", ""],
        ["ID", "Destinatário", "Mensagem", "Canal", "Enviado em", "Entregue em", "Status"],
      ];

      shippingData.forEach(item => {
        excelData.push([
          item.id || '',
          item.number || '',
          item.message || '',
          item.whatsapp?.name || '-',
          item.createdAt ? new Date(item.createdAt).toLocaleString('pt-BR') : '',
          item.deliveredAt ? new Date(item.deliveredAt).toLocaleString('pt-BR') : '',
          item.deliveredAt ? 'Entregue' : 'Pendente'
        ]);
      });

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(excelData);

      ws['!cols'] = [
        { wch: 15 },
        { wch: 20 },
        { wch: 50 },
        { wch: 25 },
        { wch: 20 },
        { wch: 20 },
        { wch: 15 }
      ];

      ws['!merges'] = [
        { s: { c: 0, r: 0 }, e: { c: 6, r: 0 } },
        { s: { c: 0, r: 1 }, e: { c: 6, r: 1 } },
        { s: { c: 0, r: 2 }, e: { c: 6, r: 2 } }
      ];

      XLSX.utils.book_append_sheet(wb, ws, "Relatório de Campanha PRO");

      const fileName = `relatorio_campanha_pro_${campaign.name?.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;

      XLSX.writeFile(wb, fileName);
      
      toast.success(`Relatório Excel gerado com sucesso! ${shippingData.length} registros exportados.`);
      
    } catch (error) {
      console.error("Erro ao exportar para Excel:", error);
      toast.error("Erro ao gerar relatório Excel");
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Grid style={{ width: "99.6%" }} container>
          <Grid xs={12} item style={{ display: "flex", alignItems: "center" }}>
            <Button
              variant="outlined"
              color="primary"
              style={{ marginRight: 10 }}
              onClick={() => history.push('/campaigns-pro')}
              startIcon={<ArrowBackIcon />}
            >
              Voltar
            </Button>
            <Title>🚀 Relatório de {campaign.name || "Campanha PRO"}</Title>
          </Grid>
        </Grid>
      </MainHeader>
      
      {/* Card com resumo e status */}
      <Paper className={classes.mainPaper} variant="outlined">
        <Box mb={2}>
          <Typography variant="h5" component="h2" gutterBottom>
            {campaign.name || "Campanha PRO"}
          </Typography>
          <Chip 
            label={formatStatus(campaign.status)} 
            color={campaign.status === "FINALIZADA" ? "primary" : "default"}
            style={{ fontWeight: 'bold', marginRight: 8 }}
          />
        </Box>
        
        <Grid container spacing={3} className={classes.summaryCards}>
          {/* Card de Status */}
          <Grid item xs={12} md={6} lg={3}>
            <Card className={classes.summaryCard} variant="outlined">
              <CardContent>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  Status de Entrega
                </Typography>
                <Box className={classes.pieChartContainer}>
                  <Doughnut data={chartData} options={chartOptions} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          {/* Cards com estatísticas principais */}
          <Grid item xs={12} md={6} lg={3}>
            <Card className={classes.summaryCard} variant="outlined">
              <CardContent>
                <SendIcon className={classes.cardIcon} />
                <Typography variant="h4" component="div">
                  {delivered}
                </Typography>
                <Typography color="textSecondary">
                  Mensagens Enviadas
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6} lg={3}>
            <Card className={classes.summaryCard} variant="outlined">
              <CardContent>
                <PhoneIcon className={classes.cardIcon} />
                <Typography variant="h4" component="div">
                  {uniqueNumbers}
                </Typography>
                <Typography color="textSecondary">
                  Destinatários Únicos
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6} lg={3}>
            <Card className={classes.summaryCard} variant="outlined">
              <CardContent>
                <CheckCircleIcon className={classes.cardIcon} />
                <Typography variant="h4" component="div">
                  {delivered}
                </Typography>
                <Typography color="textSecondary">
                  Mensagens Entregues
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        
        {/* Barra de progresso */}
        <Box className={classes.progressContainer}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs>
              <Typography variant="body2" color="textSecondary">Progresso de Entrega</Typography>
            </Grid>
            <Grid item>
              <Typography variant="body2" color="textPrimary">
                {delivered} de {totalMessagesSent}
              </Typography>
            </Grid>
          </Grid>
          <LinearProgress
            variant="determinate"
            style={{ height: 10, borderRadius: 5, margin: "8px 0" }}
            value={(delivered / (totalMessagesSent || 1)) * 100}
          />
        </Box>
        
        {/* Informações adicionais da campanha */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card variant="outlined" className={classes.detailsCard}>
              <CardHeader
                avatar={
                  <Avatar className={classes.avatar}>
                    <WhatsAppIcon />
                  </Avatar>
                }
                title="Conexão WhatsApp"
                subheader={campaign.whatsapp?.name || "Não especificado"}
              />
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card variant="outlined" className={classes.detailsCard}>
              <CardHeader
                avatar={
                  <Avatar className={classes.avatar}>
                    <ListAltIcon />
                  </Avatar>
                }
                title="Lista de Contatos"
                subheader={campaign.contactListPro?.name || "Não especificado"}
              />
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card variant="outlined" className={classes.detailsCard}>
              <CardHeader
                avatar={
                  <Avatar className={classes.avatar}>
                    <ScheduleIcon />
                  </Avatar>
                }
                title="Agendamento"
                subheader={datetimeToClient(campaign.scheduledAt) || "Não agendado"}
              />
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card variant="outlined" className={classes.detailsCard}>
              <CardHeader
                avatar={
                  <Avatar className={classes.avatar}>
                    <EventAvailableIcon />
                  </Avatar>
                }
                title="Conclusão"
                subheader={datetimeToClient(campaign.completedAt) || "Não concluído"}
              />
            </Card>
          </Grid>
        </Grid>
        
        {/* Seção de detalhes de mensagens */}
        <Box mt={4}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" className={classes.sectionTitle}>
              <MessageIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
              Detalhes das Mensagens
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<GetAppIcon />}
              onClick={exportToExcel}
              style={{
                borderRadius: 20,
                textTransform: 'none',
                fontWeight: 'bold',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                background: 'linear-gradient(45deg, #4CAF50 30%, #45a049 90%)',
              }}
            >
              Exportar Excel
            </Button>
          </Box>
          <Divider />
          
          <TableContainer className={classes.tableContainer}>
            <Table aria-label="tabela de mensagens">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Destinatário</TableCell>
                  <TableCell>Mensagem</TableCell>
                  <TableCell>Canal</TableCell>
                  <TableCell>Enviado em</TableCell>
                  <TableCell>Entregue em</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {messageRows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.jobId}</TableCell>
                    <TableCell>
                      <Tooltip title={row.number}>
                        <span>{formatPhoneNumber(row.number)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={row.fullMessage}>
                        <span>{row.message}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{row.canal || '-'}</TableCell>
                    <TableCell>{row.createdAt}</TableCell>
                    <TableCell>{row.deliveredAt || '-'}</TableCell>
                    <TableCell>{getMessageStatusChip(row.status)}</TableCell>
                  </TableRow>
                ))}
                {messageRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      {loading ? "Carregando..." : "Aguardando processamento da campanha..."}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={totalMessagesSent}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(event, newPage) => {
                setPage(newPage);
                const serverPage = newPage + 1;
                loadMoreShipping(serverPage, rowsPerPage);
              }}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Linhas por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            />
          </TableContainer>
        </Box>
      </Paper>
    </MainContainer>
  );
};

export default CampaignProReport;
